import API_BASE_URL from './config';
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

function RegisterDog({ playerId }) {
  const { state } = useLocation();
  const navigate = useNavigate();
  const puppy = useMemo(() => state?.puppy || {}, [state]);
  const litterId = state?.litterId;
  const puppyIdx = state?.puppyIdx;
  const sireDogIdFromLitter = state?.sireId;
  const damDogIdFromLitter = state?.damId;
  const optOutHealthTests = state?.optOutHealthTests || false;

  const memoizedPuppy = useMemo(() => puppy, [puppy]);

  const [registeredName, setRegisteredName] = useState(puppy.registeredName || '');
  const [callName, setCallName] = useState(puppy.name || '');
  const [breedMix, setBreedMix] = useState([{ breed: '', percentage: '' }]);
  const [sireDogId, setSireDogId] = useState(sireDogIdFromLitter || puppy.sireId || '');
  const [damDogId, setDamDogId] = useState(damDogIdFromLitter || puppy.damId || '');
  const [stashLink, setStashLink] = useState('');
  const [gender, setGender] = useState(puppy.gender || '');
  const [usingItems, setUsingItems] = useState(false);
  const [itemsUsed, setItemsUsed] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [message, setMessage] = useState('');
  const [availableBreeds, setAvailableBreeds] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);

  useEffect(() => {
    console.log('Puppy state:', memoizedPuppy);
    console.log('Initial gender:', memoizedPuppy.gender);
    console.log('Litter ID:', litterId);
    console.log('Breeder check:', { playerId, breederId: memoizedPuppy.breederId, isBreeder: playerId === memoizedPuppy.breederId });

    if (!playerId) return;

    fetch(`${API_BASE_URL}/breeds`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => {
        if (!res.ok) throw new Error(`Fetch breeds failed: ${res.status}`);
        return res.json();
      })
      .then(data => setAvailableBreeds(data.sort((a, b) => a.breedName.localeCompare(b.breedName))))
      .catch(err => setMessage('Failed to fetch breeds: ' + err.message));

    fetch(`${API_BASE_URL}/my-inventory/${playerId}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => {
        if (!res.ok) throw new Error(`Fetch inventory failed: ${res.status}`);
        return res.json();
      })
      .then(data => {
        console.log('Inventory response:', data);
        setInventoryItems(data.items || []);
      })
      .catch(err => setMessage('Failed to fetch inventory: ' + err.message));
  }, [playerId, litterId, memoizedPuppy]);

  const addBreed = () => {
    if (breedMix.length < 8) {
      setBreedMix([...breedMix, { breed: '', percentage: '' }]);
    } else {
      setMessage('Maximum 8 breeds allowed.');
    }
  };
  const removeBreed = (index) => {
  if (breedMix.length === 1) {
    setMessage("You must keep at least one breed entry.");
    return;
  }
  const newMix = breedMix.filter((_, i) => i !== index);
  setBreedMix(newMix);
};

  const updateBreedMix = (index, field, value) => {
    const newMix = [...breedMix];
    newMix[index][field] = field === 'percentage' ? value.replace(/[^0-9]/g, '') : value;
    setBreedMix(newMix);
  };

  const calculateTotalPercentage = () => {
    return breedMix.reduce((sum, mix) => sum + (parseInt(mix.percentage) || 0), 0);
  };

  // === NEW: BLOCK SAME-BREED MIXES ===
if (!litterId) {
  const selectedBreeds = breedMix.map(m => m.breed).filter(Boolean);
  const uniqueBreeds = new Set(selectedBreeds);
  
  if (selectedBreeds.length !== uniqueBreeds.size) {
    setMessage('You cannot use the same breed more than once in a mix. For a 100% purebred dog, just use ONE breed entry with 100%.');
    return;
  }
}

  const handleItemToggle = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const handleSubmit = async () => {
    if (!stashLink) {
      setMessage('Please provide a Sta.sh link.');
      return;
    }
    if (litterId && !memoizedPuppy.gender) {
      console.error('Puppy gender missing:', memoizedPuppy);
      setMessage('Puppy gender is missing from litter data. Please contact support.');
      return;
    }
    if (!litterId && !gender) {
      setMessage('Please select gender.');
      return;
    }
    if (!litterId && breedMix.some(b => !b.breed)) {
      setMessage('Please select a breed for each entry.');
      return;
    }
    if (usingItems && !itemsUsed) {
      setMessage('Please describe the edits made with the item.');
      return;
    }
    if (usingItems && selectedItems.length === 0) {
      setMessage('Please select at least one item to use.');
      return;
    }

    if (!litterId) {
      const totalPercentage = calculateTotalPercentage();
      if (totalPercentage !== 100) {
        setMessage('Breed percentages must equal exactly 100%.');
        return;
      }
    }

    const breedString = !litterId 
      ? breedMix.map(b => 
          b.percentage ? `${b.breed} (${b.percentage}%)` : b.breed
        ).join(' x ')
      : undefined;

    try {
      let url = litterId && puppyIdx !== undefined 
        ? `${API_BASE_URL}/register-puppy/${litterId}/${puppyIdx}` 
        : `${API_BASE_URL}/add-dog`;
      
      const body = {
        playerId,
        registeredName,
        callName,
        ...(litterId ? { 
          breederId: memoizedPuppy.breederId,
          breed: memoizedPuppy.breed,
          gender: memoizedPuppy.gender,
          color: memoizedPuppy.color,
          personality: memoizedPuppy.personality,
          phenotype: memoizedPuppy.phenotype,
          genotype: memoizedPuppy.genotype,
          healthTestRecords: memoizedPuppy.healthTestRecords || []
        } : { 
          breed: breedString, 
          sireDogId, 
          damDogId 
        }),
        stashLink,
        gender: litterId ? memoizedPuppy.gender : gender,
        usingItems,
        itemsUsed,
        ...(usingItems ? { usedItemIds: selectedItems } : {})
      };

      console.log('Submitting dog/puppy:', { url, body });

      const response = await fetch(url, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      setMessage(data.message);

      setRegisteredName('');
      setCallName('');
      setBreedMix([{ breed: '', percentage: '' }]);
      setSireDogId('');
      setDamDogId('');
      setStashLink('');
      setGender('');
      setUsingItems(false);
      setItemsUsed('');
      setSelectedItems([]);

      navigate('/my-dogs');
    } catch (error) {
      setMessage('Registration failed: ' + error.message);
    }
  };

  if (!playerId) return <p style={{ textAlign: 'center', color: '#888' }}>Please log in to register a dog!</p>;

  const isBreeder = litterId && playerId === memoizedPuppy.breederId;

  const inputStyle = {
    width: '100%',
    padding: '8px',
    marginBottom: '15px',
    borderRadius: '5px',
    border: '1px solid #BBDEFB',
    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
    fontSize: '1em'
  };

  const selectStyle = {
    ...inputStyle,
    appearance: 'none',
    background: 'white url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'5\'><polyline points=\'0,0 5,5 10,0\' style=\'fill:none;stroke:%231E88E5;stroke-width:2\'/></svg>") no-repeat right 10px center',
    cursor: 'pointer'
  };

  const disabledInputStyle = {
    ...inputStyle,
    backgroundColor: '#E6F0FA',
    color: '#888',
    cursor: 'not-allowed',
    opacity: 0.6,
    pointerEvents: 'none'
  };

  const buttonStyle = {
    padding: '10px 20px',
    backgroundColor: '#1E88E5',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'background-color 0.3s',
    width: '100%'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '5px',
    fontWeight: 'bold',
    color: '#1E88E5'
  };

  console.log('RegisteredName input state:', { registeredName, isBreeder, litterId, renderedBranch: isBreeder || !litterId ? 'editable' : 'locked' });

  return (
    <div style={{ 
      maxWidth: '600px', 
      margin: '0 auto', 
      padding: '20px', 
      backgroundColor: '#f9f9f9', 
      borderRadius: '8px', 
      boxShadow: '0 2px 5px rgba(0,0,0,0.1)', 
      border: '1px solid #E6F0FA' 
    }}>
      <h1 style={{ 
        color: '#1E88E5', 
        textAlign: 'center', 
        marginBottom: '20px', 
        fontSize: '1.8em' 
      }}>
        Register {litterId ? 'Puppy' : 'Dog'}{memoizedPuppy.name ? ` - ${memoizedPuppy.name}` : ''}
      </h1>
      {message && (
        <p style={{ 
          backgroundColor: '#BBDEFB', 
          padding: '10px', 
          borderRadius: '5px', 
          color: '#333', 
          textAlign: 'center', 
          marginBottom: '20px' 
        }}>
          {message}
        </p>
      )}
      {litterId && optOutHealthTests && (
        <p style={{ 
          backgroundColor: '#FFF3CD', 
          padding: '10px', 
          borderRadius: '5px', 
          color: '#856404', 
          textAlign: 'center', 
          marginBottom: '20px' 
        }}>
          Litter did not participate in DFG Inherited Disease Panel.
        </p>
      )}
      {litterId && memoizedPuppy.healthTestRecords?.length > 0 && !optOutHealthTests && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ color: '#4CAF50', marginBottom: '10px' }}>Health Tests</h3>
          <ul style={{ listStyle: 'none', padding: '0' }}>
            {memoizedPuppy.healthTestRecords.map((test, idx) => (
              <li key={idx} style={{ color: '#333', marginBottom: '5px' }}>
                {test.testName.replace(/^BBP\s*-\s*/i, '')}: {test.result}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div>
        <label style={labelStyle}>Registered Name:</label>
        {isBreeder || !litterId ? (
          <input
            type="text"
            value={registeredName}
            onChange={(e) => setRegisteredName(e.target.value)}
            placeholder="Registered Name"
            style={inputStyle}
          />
        ) : (
          <input
            type="text"
            value={memoizedPuppy.registeredName || ''}
            disabled
            readOnly
            tabIndex={-1}
            style={disabledInputStyle}
          />
        )}
        {litterId && !isBreeder && (
          <p style={{ 
            backgroundColor: '#FFF3CD', 
            padding: '5px', 
            borderRadius: '5px', 
            color: '#856404', 
            fontSize: '0.9em', 
            marginTop: '5px' 
          }}>
            The registered name is set by the breeder and cannot be changed.
          </p>
        )}
      </div>
      <div>
        <label style={labelStyle}>Call Name:</label>
        <input
          type="text"
          value={callName}
          onChange={(e) => setCallName(e.target.value)}
          placeholder="Call Name"
          style={inputStyle}
        />
      </div>
      {litterId && memoizedPuppy.breed ? (
        <div>
          <label style={labelStyle}>Breed (from litter):</label>
          <input
            type="text"
            value={memoizedPuppy.breed}
            disabled
            style={disabledInputStyle}
          />
        </div>
      ) : (
        <div>
          <label style={labelStyle}>Breeds (up to 8, must total 100%):</label>
          
          {breedMix.map((mix, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              {/* Breed Select */}
              <select
                value={mix.breed}
                onChange={(e) => updateBreedMix(idx, 'breed', e.target.value)}
                style={{ ...selectStyle, flex: '2' }}
              >
                <option value="">Select Breed</option>
                {availableBreeds.map(b => (
                  <option key={b._id} value={b.breedName}>{b.breedName}</option>
                ))}
              </select>

              {/* Percentage Input */}
              <input
                type="text"
                value={mix.percentage}
                onChange={(e) => updateBreedMix(idx, 'percentage', e.target.value)}
                placeholder="%"
                style={{ ...inputStyle, flex: '1', maxWidth: '80px' }}
              />

              {/* REMOVE BUTTON */}
              {breedMix.length > 1 && (
                <button
                  onClick={() => removeBreed(idx)}
                  style={{
                    backgroundColor: '#FF4444',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9em',
                    fontWeight: 'bold'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#CC0000'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#FF4444'}
                >
                  Remove
                </button>
              )}
            </div>
          ))}

          <p style={{ color: '#333', marginBottom: '10px', fontWeight: 'bold' }}>
            Total: {calculateTotalPercentage()}%
          </p>

          <button
            onClick={addBreed}
            disabled={breedMix.length >= 8}
            style={{
              ...buttonStyle,
              backgroundColor: breedMix.length >= 8 ? '#ccc' : '#4CAF50',
              width: 'auto',
              padding: '10px 20px',
              fontSize: '0.95em'
            }}
          >
            + Add Another Breed
          </button>
        </div>
      )}

      {!litterId ? (
        <>
          <div>
            <label style={labelStyle}>Sire ID (optional):</label>
            <input
              type="text"
              value={sireDogId}
              onChange={(e) => setSireDogId(e.target.value)}
              placeholder="e.g., CHI002-001"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Dam ID (optional):</label>
            <input
              type="text"
              value={damDogId}
              onChange={(e) => setDamDogId(e.target.value)}
              placeholder="e.g., APBT001-001"
              style={inputStyle}
            />
          </div>
        </>
      ) : (
        <>
          <div>
            <label style={labelStyle}>Sire ID (from litter):</label>
            <input
              type="text"
              value={sireDogIdFromLitter}
              disabled
              style={disabledInputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Dam ID (from litter):</label>
            <input
              type="text"
              value={damDogIdFromLitter}
              disabled
              style={disabledInputStyle}
            />
          </div>
        </>
      )}
      <div>
        <label style={labelStyle}>Sta.sh Link:</label>
        <input
          type="url"
          value={stashLink}
          onChange={(e) => setStashLink(e.target.value)}
          placeholder="https://sta.sh/example"
          style={inputStyle}
          required
        />
      </div>
      <div>
        <label style={labelStyle}>{litterId ? 'Gender (from litter):' : 'Gender:'}</label>
        {litterId ? (
          <input
            type="text"
            value={memoizedPuppy.gender || 'Unknown'}
            disabled
            style={disabledInputStyle}
          />
        ) : (
          <select
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            style={selectStyle}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        )}
      </div>
      {litterId && memoizedPuppy.personality && (
        <div>
          <label style={labelStyle}>Personality (from litter):</label>
          <input
            type="text"
            value={memoizedPuppy.personality}
            disabled
            style={disabledInputStyle}
          />
        </div>
      )}
      {litterId && memoizedPuppy.phenotype && (
        <div>
          <label style={labelStyle}>Phenotype (from litter):</label>
          <input
            type="text"
            value={memoizedPuppy.phenotype}
            disabled
            style={disabledInputStyle}
          />
        </div>
      )}
      {litterId && memoizedPuppy.genotype && (
        <div>
          <label style={labelStyle}>Genotype (from litter):</label>
          <input
            type="text"
            value={memoizedPuppy.genotype}
            disabled
            style={disabledInputStyle}
          />
        </div>
      )}
      <div>
        <label style={{ ...labelStyle, display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
          <input
            type="checkbox"
            checked={usingItems}
            onChange={(e) => setUsingItems(e.target.checked)}
            style={{ margin: '0' }}
          />
          Use Edit Item
        </label>
      </div>
      {usingItems && (
        <div>
          {/* Items selection and description */}
          <div>
            <label style={labelStyle}>Select Items to Use:</label>
            {inventoryItems.filter(item => ['CUSTOM_EAR_CROP', 'BODY_MODIFICATIONS'].includes(item.itemId) && item.quantity > 0).map(item => (
              <div key={item.itemId} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                <input
                  type="checkbox"
                  checked={selectedItems.includes(item.itemId)}
                  onChange={() => handleItemToggle(item.itemId)}
                  style={{ marginRight: '10px' }}
                />
                <span>{item.name} (Qty: {item.quantity})</span>
              </div>
            ))}
          </div>
          <div>
            <label style={labelStyle}>Describe Edits:</label>
            <textarea
              value={itemsUsed}
              onChange={(e) => setItemsUsed(e.target.value)}
              placeholder="e.g., Cropped ears to pointy shape"
              style={{ ...inputStyle, height: '100px', resize: 'vertical' }}
            />
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        <button
          onClick={handleSubmit}
          style={buttonStyle}
          onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#1E88E5'}
        >
          {litterId ? 'Register Puppy' : 'Submit Dog'}
        </button>
        <button
          onClick={() => navigate('/my-litters')}
          style={{
            ...buttonStyle,
            backgroundColor: '#E57373',
            width: 'auto'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#D32F2F'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#E57373'}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default RegisterDog;